<template>
    <div>
        <nav
            class="mx-auto flex gap-x-4 max-w-7xl items-center justify-between p-6 lg:px-8"
            aria-label="Global">
            <div class="hidden lg:flex">
                <a
                    href="/"
                    class="-m-1.5 p-1.5">
                    <LogoImage class="h-5" />
                </a>
            </div>
            <div class="flex lg:hidden">
                <button
                    type="button"
                    class="-m-2.5 p-2.5 lg:hidden text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200"
                    @click="layoutStore.isSidebarOpen = true">
                    <span class="sr-only">Open sidebar</span>
                    <Icon
                        name="heroicons:bars-3"
                        class="h-6 w-6"
                        aria-hidden="true" />
                </button>
            </div>
            <div class="hidden lg:flex lg:flex-1 lg:gap-x-12 align-center justify-center">
                <div
                    v-for="(item, index) in headerMenuFiltered"
                    :key="item.title"
                    class="flex align-center justify-center">
                    <NuxtLink
                        v-if="item.path"
                        class="text-sm font-semibold leading-6 hover:text-primary focus:text-primary"
                        :class="currentPath(item.path) ? 'text-primary' : 'text-gray-900 dark:text-gray-100'"
                        :to="(item.isCmsPath) ? '/' + item.path : useLocalePath()(item.path)">
                        {{ titles[index].name }}
                    </NuxtLink>

                    <a
                        v-else-if="item.function"
                        href="#"
                        class="text-sm font-semibold leading-6 text-gray-900 hover:text-primary focus:text-primary dark:text-gray-100"
                        @click.prevent="item.function()">
                        {{ titles[index].name }}
                    </a>

                    <PopoverGroup
                        v-if="item.children && item.children.length > 0"
                        class="hidden lg:flex">
                        <Popover
                            v-slot="{ open, close }"
                            class="relative flex align-center justify-center">
                            <PopoverButton
                                class="flex items-center gap-x-1 text-sm/6 font-semibold focus-visible:outline-none"
                                :class="parentPath(item.children) ? 'text-primary' : 'text-gray-900 dark:text-gray-100'">
                                <Icon
                                    name="heroicons:chevron-down"
                                    class="ml-1 h-5 w-5 text-gray-400 transition-transform duration-150 ease-in-out"
                                    :class="{'rotate-180': open}"
                                    aria-hidden="true" />
                            </PopoverButton>

                            <Transition
                                enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                                <PopoverPanel class="absolute -left-4 top-full z-[99] mt-3 w-40 max-w-md overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-gray-900/5">
                                    <div
                                        class="text-sm text-gray-700">
                                        <div
                                            v-for="(child, childIndex) in item.children"
                                            :key="child.title"
                                            :class="currentPath(child.path) ? 'bg-gray-100' : 'hover:bg-gray-100'"
                                            class="group px-4 py-2 relative flex items-center gap-x-6 p-4 text-sm/6">
                                            <NuxtLink
                                                v-if="child.path"
                                                class="text-sm w-full leading-6 "
                                                :to="(child.isCmsPath) ? '/' + child.path : useLocalePath()(child.path)"
                                                @click="close()">
                                                {{ titles[index].children[childIndex].name }}
                                            </NuxtLink>

                                            <a
                                                v-else-if="child.function"
                                                href="#"
                                                class="text-sm w-full leading-6"
                                                @click.prevent="child.function(); close()">
                                                {{ titles[index].children[childIndex].name }}
                                            </a>
                                        </div>
                                    </div>
                                </PopoverPanel>
                            </Transition>
                        </Popover>
                    </PopoverGroup>
                </div>
            </div>
            <div
                v-if="authStore.isAuthenticated"
                class="flex gap-x-4 lg:gap-x-6 justify-end items-center">
                <ServiceSwitcher class="hidden lg:block" />

                <button
                    type="button"
                    class="-m-2.5 p-2.5 text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 relative"
                    @click="inboxOpen = !inboxOpen">
                    <span class="sr-only">View notifications</span>
                    <Icon
                        name="heroicons:bell"
                        class="h-6 w-6"
                        aria-hidden="true" />
                    <span
                        v-if="notificationStore.unreadNotificationsCount > 0"
                        class="w-2 h-2 bg-danger-500 block absolute top-3 right-3 rounded-full" />
                    <NotificationInbox v-model="inboxOpen" />
                </button>

                <!-- Separator -->
                <div
                    class="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"
                    aria-hidden="true" />

                <UserMenu />
            </div>
        </nav>

        <MobileMenuFrontend
            :header-menu-filtered="headerMenuFiltered"
            :titles="titles" />
    </div>
</template>

<script setup>
import {useNuxtApp} from "nuxt/app"
import {useI18n} from "vue-i18n"
import {useRoute} from "vue-router"
import NotificationInbox from "@/components/global/NotificationInbox.vue"
import MobileMenuFrontend from "@/components/layouts/MobileMenuFrontend.vue"
import UserMenu from "@/components/layouts/UserMenu/UserMenu.vue"
import {useAuthStore} from "@/store/auth"
import {useCmsNavItemsStore} from "@/store/cmsNavItems"
import {useLayoutStore} from "@/store/layout"
import {useNotificationStore} from "@/store/notifications"

const inboxOpen = ref(false)
const {t: $t} = useI18n()
const cmsNavItemsStore = useCmsNavItemsStore()

const notificationStore = useNotificationStore()
const layoutStore = useLayoutStore()
const authStore = useAuthStore()
const route = useRoute()
const {$can} = useNuxtApp()

const headerMenuFiltered = computed(() => {
    let withCmsItems = false
    if (cmsNavItemsStore.cmsNavItems?.length > 0) {
        withCmsItems = true
    }
    return getHeaderMenu(withCmsItems).filter(item =>
        ( // Filter out statistics if no statistic survey is available
            (
                item.path === 'statistics' && authStore?.userData?.apply_statistic_survey_available
            ) ||
            item.path !== 'statistics'
        ) && (
            (
                authStore.isAuthenticated &&
                item.showAuthenticated &&
                (
                    !item.ability ||
                    $can(item.ability[0], item.ability[1])
                )
            ) ||
            (
                !authStore.isAuthenticated &&
                item.showUnauthenticated
            )
        )
    )
})

const titles = computed(() => {
    const titles = []
    for (let i = 0; i < headerMenuFiltered.value.length; i++) {
        if (headerMenuFiltered.value[i].title) {
            titles.push(
                {"name": $t(headerMenuFiltered.value[i].title)}
            )
        } else if (headerMenuFiltered.value[i].titleRaw) {
            titles.push(
                {"name": headerMenuFiltered.value[i].titleRaw}
            )
        } else {
            titles.push({"name": ''})
        }

        if (headerMenuFiltered.value[i].children && headerMenuFiltered.value[i].children.length > 0) {
            titles[i].children = []
            for (let j = 0; j < headerMenuFiltered.value[i].children.length; j++) {
                if (headerMenuFiltered.value[i].children[j].title) {
                    titles[i].children.push(
                        {"name": $t(headerMenuFiltered.value[i].children[j].title)}
                    )
                } else if (headerMenuFiltered.value[i].children[j].titleRaw) {
                    titles[i].children.push(
                        {"name": headerMenuFiltered.value[i].children[j].titleRaw}
                    )
                } else {
                    titles[i].children.push({"name": ''})
                }
            }
        }
    }
    return titles
})

const currentPath = (path) => {
    if (path === '/') {
        return route.path === path
    }
    return route.path === '/' + path
}

const parentPath = (children) => {
    return children?.some(child => currentPath(child.path))
}
</script>